import { t } from "../lib/i18n";
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

interface PropertyInsight {
  id: string;
  insight_type: string;
  summary: string;
  details: any; // JSONB field, can be any structure
  extracted_from_text: string;
  created_at: string;
}

interface PropertyInsightsProps {
  propertyId: string;
  className?: string; // Add className prop
}

const PropertyInsights: React.FC<PropertyInsightsProps> = ({ propertyId, className }) => {
  const [insights, setInsights] = useState<PropertyInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSnippets, setExpandedSnippets] = useState<{[key: string]: boolean}>({});

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/properties/${propertyId}/insights`);
      setInsights(response.data);
    } catch (err) {
      console.error('Error fetching property insights:', err);
      setError('Failed to load property insights.');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) {
      fetchInsights();
    }
  }, [propertyId, fetchInsights]);

  const toggleSnippet = (id: string) => {
    setExpandedSnippets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderDetails = (details: any) => {
    if (typeof details === 'string') {
      try {
        const parsedDetails = JSON.parse(details);
        return JSON.stringify(parsedDetails, null, 2);
      } catch (e) {
        return details; // Not a valid JSON string, display as is
      }
    }
    return JSON.stringify(details, null, 2);
  };

  const renderSnippet = (text: string, id: string) => {
    const lines = text.split('\n');
    const showFull = expandedSnippets[id];
    const displayLines = 5;

    if (lines.length <= displayLines && text.length < 500) { // Also consider character length for very long single lines
      return text;
    }

    const truncatedText = lines.slice(0, displayLines).join('\n') + '...';

    return (
      <>
        {showFull ? text : truncatedText}
        <button 
          onClick={() => toggleSnippet(id)} 
          className="text-blue-500 hover:underline ml-2 text-xs"
        >
          {showFull ? t("propertyInsights.k1") : t("propertyInsights.k2")}
        </button>
      </>
    );
  };

  if (loading) {
    return <div>{t("propertyInsights.k3")}</div>;
  }

  if (error) {
    return <div className="text-danger">{t("propertyInsights.k4")} {error}</div>;
  }

  return (
    <div className={`mt-8 ${className}`}>
      <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("propertyInsights.k5")}</h2>
      {insights.length === 0 ? (
        <p className="text-text-secondary">{t("propertyInsights.k6")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {insights.map((insight) => (
            <div key={insight.id} className="bg-bg-secondary p-4 rounded-lg shadow border border-border-primary">
              <h3 className="text-lg font-semibold text-text-primary mb-2">{insight.insight_type}</h3>
              <p className="text-text-secondary mb-2">{insight.summary}</p>
              {insight.details && (
                <div className="text-sm text-text-tertiary">
                  <strong>{t("propertyInsights.k7")}</strong>
                  <pre className="whitespace-pre-wrap break-words bg-bg-primary p-2 rounded mt-1">{renderDetails(insight.details)}</pre>
                </div>
              )}
              {insight.extracted_from_text && (
                <div className="text-xs text-text-quaternary mt-2">
                  <strong>{t("propertyInsights.k8")}</strong> "{renderSnippet(insight.extracted_from_text, insight.id)}"
                </div>
              )}
              <div className="text-xs text-text-quaternary mt-2">
                {t("propertyInsights.k9")} {new Date(insight.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyInsights;
